    import React, { useEffect, useState } from 'react'
    import moment from 'moment';
    import { addDays } from "date-fns";
    import { Calendar } from 'react-date-range';
    import DateRangeIcon from '@mui/icons-material/DateRange';
    import {
        Grid,
        TextField,
        FormControlLabel,
        Checkbox,
        MenuItem,
        Menu,
        InputAdornment,
        CircularProgress,
        Button
    } from '@mui/material';
    import SnackbarToast from '../../../_common/SnackbarToast';

    const MAX_INSTANT_DISCOUNT_AMOUNT = 1000000;
    const MAX_VELOCITY_TRANSACTION_COUNT = 1000000;

    const EMPTY_RULE_HOUR = {
        startTime: '',
        endTime: ''
    };

    const EMPTY_DISCOUNT_FUNDING = {
        merchantValue: 0,
        issuerValue: 0,
        brandValue: 0
    };

    const EMPTY_VELOCITY_CHECK = {
        maxTransactionCount: 0,
        validityType: '0'
    };

    const hasValue = (value) => value !== undefined && value !== null && value !== '';

    const isEnabledFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

    const isRequiredFieldFilled = (value) => {
        if (Array.isArray(value)) return value.length > 0;
        return hasValue(value) && !(typeof value === 'string' && value.trim() === '');
    };

    const isValidAmountInput = (value) => {
        if (value === '') return true;
        return value.length < 16 && /^-?\d*\.?\d*$/.test(value) && (value.split(".").length < 2 || value.split(".")[1].length < 4);
    };

    const isValidIntegerInput = (value) => {
        if (value === '') return true;
        return /^-?\d*$/.test(value);
    };

    const isCompleteNumericValue = (value) => /^-?\d+(\.\d+)?$/.test(`${value}`);

    const isNegativeInput = (value) => `${value}`.trim().startsWith('-');

    const getDiscountFundingError = (fieldLabel, value, maxValue) => {
        if (!hasValue(value)) return '';
        if (isNegativeInput(value)) return `${fieldLabel} cannot be negative`;
        if (!isValidAmountInput(`${value}`) || !isCompleteNumericValue(value)) return `${fieldLabel} has invalid format`;
        if (Number(value) > maxValue) return `${fieldLabel} cannot be greater than ${maxValue}`;

        return '';
    };

    const getVelocityMaxTransactionCountError = (value) => {
        if (!hasValue(value)) return '';
        if (isNegativeInput(value)) return 'Max Transaction Count must be greater than 0';
        if (!/^\d+$/.test(`${value}`)) return 'Max Transaction Count must be a positive whole number';
        if (Number(value) <= 0) return 'Max Transaction Count must be greater than 0';
        if (Number(value) > MAX_VELOCITY_TRANSACTION_COUNT) return `Max Transaction Count cannot be greater than ${MAX_VELOCITY_TRANSACTION_COUNT}`;

        return '';
    };

    const getDateTimeLocalValue = (date, dayBoundary) => {
        if (!date) return '';
        const parsedDate = new Date(date);
        if (Number.isNaN(parsedDate.getTime())) return '';
        if (dayBoundary === 'start') {
            parsedDate.setHours(0, 0, 0, 0);
        } else if (dayBoundary === 'end') {
            parsedDate.setHours(23, 59, 0, 0);
        }

        const pad = (value) => String(value).padStart(2, '0');
        return `${parsedDate.getFullYear()}-${pad(parsedDate.getMonth() + 1)}-${pad(parsedDate.getDate())}T${pad(parsedDate.getHours())}:${pad(parsedDate.getMinutes())}`;
    };

    const getMaxDateTime = (...values) => {
        const validValues = values.filter(Boolean);
        if (validValues.length === 0) return '';

        return validValues.reduce((maxValue, value) => {
            return new Date(value).getTime() > new Date(maxValue).getTime() ? value : maxValue;
        });
    };

    const getDateFromDateTime = (value) => {
        if (!value) return null;
        const parsedDate = new Date(value);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    };

    const getStartOfDayTime = (value) => {
        const parsedDate = new Date(value);
        parsedDate.setHours(0, 0, 0, 0);
        return parsedDate.getTime();
    };

    const getAmountError = (field, minAmount, maxAmount) => {
        const value = field === 'minAmount' ? minAmount : maxAmount;

        if (!hasValue(value)) return '';
        if (isNegativeInput(value)) return 'Amount must be greater than 0';
        if (!isValidAmountInput(`${value}`) || !isCompleteNumericValue(value)) return 'Invalid amount format';
        if (Number(value) <= 0) return 'Amount must be greater than 0';
        if (Number(value) > MAX_INSTANT_DISCOUNT_AMOUNT) return `Amount cannot be greater than ${MAX_INSTANT_DISCOUNT_AMOUNT}`;
        if (hasValue(minAmount) && hasValue(maxAmount) && Number(maxAmount) <= Number(minAmount)) {
            return 'Max Amount must be greater than Min Amount';
        }

        return '';
    };

    const getDateError = (field, startDateTime, endDateTime, minSelectableDateTime) => {
        const value = field === 'startDateTime' ? startDateTime : endDateTime;

        if (!hasValue(value)) return '';
        if (minSelectableDateTime && new Date(value).getTime() < new Date(minSelectableDateTime).getTime()) {
            return `${field === 'startDateTime' ? 'Start' : 'End'} Date must be from the next day onward`;
        }
        if (hasValue(startDateTime) && hasValue(endDateTime) && getStartOfDayTime(endDateTime) <= getStartOfDayTime(startDateTime)) {
            return 'End Date must be greater than Start Date';
        }

        return '';
    };

    function InstantDiscountStepper(props) {
        const {
            classes, handleNext, disableToken, setActiveStep, onChangeView, createLoad, currentForm,
            instantDiscount, setInstantDiscount, schemeStartDate
        } = props;
        
        const DisableFlag = disableToken ? disableToken : false
        const allowEdit = currentForm === "edit";
        const ruleHour = instantDiscount.ruleHour || EMPTY_RULE_HOUR;
        const discountFunding = instantDiscount.discountFunding || EMPTY_DISCOUNT_FUNDING;
        const velocityCheck = instantDiscount.velocityCheck || EMPTY_VELOCITY_CHECK;
        const applicableDays = Array.isArray(instantDiscount.applicableDays)
            ? instantDiscount.applicableDays.filter(day => day !== 'All')
            : [];
        const discountFundingType = hasValue(instantDiscount.discountFundingType) ? `${instantDiscount.discountFundingType}` : '0';
        const isRuleHourEnabled = isEnabledFlag(instantDiscount.isRuleHourEnable);
        const isVelocityCheckEnabled = isEnabledFlag(instantDiscount.isVelocityCheckEnabled);
        const schemeStartDateTime = getDateTimeLocalValue(schemeStartDate, 'start');
        const nextDayDateTime = getDateTimeLocalValue(new Date(Date.now() + (24 * 60 * 60 * 1000)), 'start');
        // In edit mode, use scheme start date as min (even if in the past) to allow existing config
        const minSelectableDateTime = allowEdit 
            ? (schemeStartDateTime || nextDayDateTime)
            : getMaxDateTime(schemeStartDateTime, nextDayDateTime);
        const startDateValue = getDateFromDateTime(instantDiscount.startDateTime);
        const endMinSelectableDateTime = getMaxDateTime(
            startDateValue ? getDateTimeLocalValue(addDays(startDateValue, 1), 'start') : '',
            minSelectableDateTime
        );

        const handleDiscountChange = (field, value) => {
            let newValue = value;
            if (field === 'minAmount' || field === 'maxAmount') {
                if (!isValidAmountInput(value)) return;
                newValue = value;
            }
            if (field === 'discountFunding.merchantValue' || field === 'discountFunding.issuerValue' || field === 'discountFunding.brandValue') {
                if (!isValidAmountInput(value)) return;
                newValue = value;
            }
            if (field === 'velocityCheck.maxTransactionCount') {
                if (!isValidIntegerInput(value)) return;
                newValue = value;
            }
            let updatedDiscount;
            if (field.includes('.')) {
                const keys = field.split('.');
                updatedDiscount = JSON.parse(JSON.stringify(instantDiscount));
                let obj = updatedDiscount;
                for (let i = 0; i < keys.length - 1; i++) {
                    if (!obj[keys[i]]) obj[keys[i]] = {};
                    obj = obj[keys[i]];
                }
                obj[keys[keys.length - 1]] = newValue;
            } else {
                updatedDiscount = { ...instantDiscount, [field]: newValue };
            }
            // Reset discount funding values when funding type actually changes to a different value
            if (field === 'discountFundingType' && newValue !== discountFundingType) {
                updatedDiscount.discountFunding = {
                    merchantValue: '',
                    issuerValue: '',
                    brandValue: ''
                };
            }
            if (field === 'isRuleHourEnable') {
                updatedDiscount.ruleHour = updatedDiscount.ruleHour || { ...EMPTY_RULE_HOUR };
            }
            if (field === 'isVelocityCheckEnabled') {
                updatedDiscount.velocityCheck = updatedDiscount.velocityCheck || { ...EMPTY_VELOCITY_CHECK };
            }
            if (field === 'applicableDays' && Array.isArray(newValue)) {
                updatedDiscount.applicableDays = newValue.filter(day => day !== 'All');
            }
            const minAmount = field === 'minAmount' ? newValue : updatedDiscount.minAmount;
            const maxAmount = field === 'maxAmount' ? newValue : updatedDiscount.maxAmount;
            const startDateTime = field === 'startDateTime' ? newValue : updatedDiscount.startDateTime;
            const endDateTime = field === 'endDateTime' ? newValue : updatedDiscount.endDateTime;
            const validationMessage =
                (field === 'velocityCheck.maxTransactionCount' ? getVelocityMaxTransactionCountError(newValue) : '') ||
                getAmountError(field, minAmount, maxAmount) ||
                getDateError(field, startDateTime, endDateTime, allowEdit ? null : minSelectableDateTime);
            updatedDiscount.isNext = validationMessage ? false : instantDiscount.isNext;
            setInstantDiscount(updatedDiscount);

            if (validationMessage) {
                setSnackBarMsg(validationMessage);
                setOpenSnackbar(true);
            }
        };
        const [startAnchorEl, setStartAnchorEl] = useState(null);
        const [endAnchorEl, setEndAnchorEl] = useState(null);
        const daysOfWeek = [
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
            'Sunday'
        ];
        
        const [openSnackbar, setOpenSnackbar] = useState(false);
        const [snackbarMsg, setSnackBarMsg] = useState(null);

        const handleSnackBarAction = () => {
            setOpenSnackbar(false);
            setSnackBarMsg(null);
        };

        const viewMode = currentForm === "view";
        const minAmountError = viewMode ? '' : getAmountError('minAmount', instantDiscount.minAmount, instantDiscount.maxAmount);
        const maxAmountError = viewMode ? '' : getAmountError('maxAmount', instantDiscount.minAmount, instantDiscount.maxAmount);
        const editMinDateTime = (allowEdit || viewMode) ? null : minSelectableDateTime;
        const startDateError = viewMode ? '' : getDateError('startDateTime', instantDiscount.startDateTime, instantDiscount.endDateTime, editMinDateTime);
        const endDateError = viewMode ? '' : getDateError('endDateTime', instantDiscount.startDateTime, instantDiscount.endDateTime, editMinDateTime);

        const isAllFundingZero = !viewMode && Number(discountFunding.merchantValue) === 0 && Number(discountFunding.issuerValue) === 0 && Number(discountFunding.brandValue) === 0;
        const fundingErrorMsg = isAllFundingZero ? "At least one funding value must be greater than 0" : "";
        const discountFundingMax = discountFundingType === '1' ? 100 : 1000000;
        const merchantFundingError = viewMode ? '' : getDiscountFundingError('Merchant Value', discountFunding.merchantValue, discountFundingMax);
        const issuerFundingError = viewMode ? '' : getDiscountFundingError('Issuer Value', discountFunding.issuerValue, discountFundingMax);
        const brandFundingError = viewMode ? '' : getDiscountFundingError('Brand Value', discountFunding.brandValue, discountFundingMax);
        const maxTransactionCountError = viewMode ? '' : getVelocityMaxTransactionCountError(velocityCheck.maxTransactionCount);


        useEffect(() => {
            const currentRuleHour = instantDiscount.ruleHour || EMPTY_RULE_HOUR;
            const currentDiscountFunding = instantDiscount.discountFunding || EMPTY_DISCOUNT_FUNDING;
            const currentVelocityCheck = instantDiscount.velocityCheck || EMPTY_VELOCITY_CHECK;
            const currentApplicableDays = Array.isArray(instantDiscount.applicableDays)
                ? instantDiscount.applicableDays.filter(day => day !== 'All')
                : [];
            const currentDiscountFundingType = hasValue(instantDiscount.discountFundingType) ? `${instantDiscount.discountFundingType}` : '0';
            const currentIsRuleHourEnabled = isEnabledFlag(instantDiscount.isRuleHourEnable);
            const currentIsVelocityCheckEnabled = isEnabledFlag(instantDiscount.isVelocityCheckEnabled);
            const requiredFields = [
                'startDateTime',
                'endDateTime',
                'minAmount',
                'maxAmount',
                'applicableDays',
                'discountFundingType',
                'discountFunding.merchantValue',
                'discountFunding.issuerValue',
                'discountFunding.brandValue'
            ];
            const normalizedInstantDiscount = {
                ...instantDiscount,
                applicableDays: currentApplicableDays,
                discountFundingType: currentDiscountFundingType,
                discountFunding: currentDiscountFunding,
                ruleHour: currentRuleHour,
                velocityCheck: currentVelocityCheck
            };
            let allFilled = requiredFields.every(field => {
                if (field.includes('.')) {
                    const [parent, child] = field.split('.');
                    const value = normalizedInstantDiscount[parent]?.[child];
                    return isRequiredFieldFilled(value);
                }
                const value = normalizedInstantDiscount[field];
                return isRequiredFieldFilled(value);
            });
            if (currentIsRuleHourEnabled) {
                allFilled = allFilled && !!currentRuleHour.startTime && !!currentRuleHour.endTime;
            }
            if (currentIsVelocityCheckEnabled) {
                allFilled = allFilled &&
                    currentVelocityCheck &&
                    currentVelocityCheck.maxTransactionCount !== undefined && currentVelocityCheck.maxTransactionCount !== '' &&
                    !!currentVelocityCheck.validityType;
            }
            const currentDiscountFundingMax = currentDiscountFundingType === '1' ? 100 : 1000000;
            const validationError = Boolean(
                getAmountError('minAmount', instantDiscount.minAmount, instantDiscount.maxAmount) ||
                getAmountError('maxAmount', instantDiscount.minAmount, instantDiscount.maxAmount) ||
                getDateError('startDateTime', instantDiscount.startDateTime, instantDiscount.endDateTime, allowEdit ? null : minSelectableDateTime) ||
                getDateError('endDateTime', instantDiscount.startDateTime, instantDiscount.endDateTime, allowEdit ? null : minSelectableDateTime) ||
                (Number(currentDiscountFunding.merchantValue) === 0 && Number(currentDiscountFunding.issuerValue) === 0 && Number(currentDiscountFunding.brandValue) === 0) ||
                Boolean(getDiscountFundingError('Merchant Value', currentDiscountFunding.merchantValue, currentDiscountFundingMax)) ||
                Boolean(getDiscountFundingError('Issuer Value', currentDiscountFunding.issuerValue, currentDiscountFundingMax)) ||
                Boolean(getDiscountFundingError('Brand Value', currentDiscountFunding.brandValue, currentDiscountFundingMax)) ||
                (currentIsVelocityCheckEnabled && Boolean(getVelocityMaxTransactionCountError(currentVelocityCheck.maxTransactionCount)))
            );
            const nextFlag = allFilled && !validationError;
            if (instantDiscount.isNext !== nextFlag) {
                setInstantDiscount((e) => ({ ...e, isNext: nextFlag }));
            }
        }, [instantDiscount, allowEdit, minSelectableDateTime, setInstantDiscount]);

    const handleDateMenuToggle = (event, type) => {
        if (type === "start") {
            startAnchorEl === null ? setStartAnchorEl(event.currentTarget) : setStartAnchorEl(null)
        } else {
            endAnchorEl === null ? setEndAnchorEl(event.currentTarget) : setEndAnchorEl(null)
        }
    }

    const handleDateSelect = (date, type) => {
        type === "start" ? setStartAnchorEl(null) : setEndAnchorEl(null);
        if (type === "start") {
            const startDateTime = getDateTimeLocalValue(date, 'start');
            const nextEndDateTime = getDateTimeLocalValue(addDays(date, 1), 'end');
            const shouldUpdateEndDate = !instantDiscount.endDateTime ||
                getStartOfDayTime(instantDiscount.endDateTime) <= getStartOfDayTime(startDateTime);

            setInstantDiscount((value) => ({
                ...value,
                startDateTime,
                endDateTime: shouldUpdateEndDate ? nextEndDateTime : value.endDateTime,
            }));
        } else {
            setInstantDiscount((value) => ({
                ...value,
                endDateTime: getDateTimeLocalValue(date, 'end'),
            }));
        }
    }

    const isViewMode = currentForm === "view";
    return (
        <React.Fragment>
            <Grid container spacing={2} style={{ marginBottom: 24 }}>
                <Grid item xs={6} className={"calender_input"}>
                    <TextField
                        label="Start Date"
                        disabled={!allowEdit ? (DisableFlag) : false}
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        value={instantDiscount.startDateTime ? moment(instantDiscount.startDateTime).format("DD-MMM-yyyy") : ""}
                        onClick={(e) => (!DisableFlag || allowEdit) && handleDateMenuToggle(e, "start")}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <DateRangeIcon className={"calender_input"} />
                                </InputAdornment>
                            ),
                        }}
                        error={Boolean(startDateError)}
                        helperText={startDateError}
                    />
                    <Menu
                        id={"instantDiscountStartDate"} anchorEl={startAnchorEl}
                        keepMounted open={Boolean(startAnchorEl)}
                        onClose={() => setStartAnchorEl(null)}
                        elevation={1}
                        anchorOrigin={{ vertical: 'top', horizontal: 'left', }}
                        classes={{ paper: classes.profileMenu, }}
                    >
                        <Calendar color="#1BA785" rangeColors={['#1BA785']}
                            onChange={(date) => handleDateSelect(date, "start")}
                            minDate={getDateFromDateTime(minSelectableDateTime)}
                        />
                    </Menu>
                </Grid>
                <Grid item xs={6} className={"calender_input"}>
                    <TextField
                        label="End Date"
                        disabled={!allowEdit ? (DisableFlag) : false}
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        value={instantDiscount.endDateTime ? moment(instantDiscount.endDateTime).format("DD-MMM-yyyy") : ""}
                        onClick={(e) => (!DisableFlag || allowEdit) && handleDateMenuToggle(e, "end")}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <DateRangeIcon className={"calender_input"} />
                                </InputAdornment>
                            ),
                        }}
                        error={Boolean(endDateError)}
                        helperText={endDateError}
                    />
                    <Menu
                        id={"instantDiscountEndDate"} anchorEl={endAnchorEl}
                        keepMounted open={Boolean(endAnchorEl)}
                        onClose={() => setEndAnchorEl(null)}
                        elevation={1}
                        anchorOrigin={{ vertical: 'top', horizontal: 'left', }}
                        classes={{ paper: classes.profileMenu, }}
                    >
                        <Calendar color="#1BA785" rangeColors={['#1BA785']}
                            onChange={(date) => handleDateSelect(date, "end")}
                            minDate={getDateFromDateTime(endMinSelectableDateTime)}
                        />
                    </Menu>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Min Amount"
                        type="number"
                        disabled={!allowEdit ? (DisableFlag) : false}
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        value={instantDiscount.minAmount}
                        onChange={e => {
                            if (e.target.value !== '' && Number(e.target.value) > MAX_INSTANT_DISCOUNT_AMOUNT) return;
                            handleDiscountChange('minAmount', e.target.value);
                        }}
                        inputProps={{ min: 1, max: MAX_INSTANT_DISCOUNT_AMOUNT }}
                        error={Boolean(minAmountError)}
                        helperText={minAmountError}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Max Amount"
                        type="number"
                        disabled={!allowEdit ? (DisableFlag) : false}
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        value={instantDiscount.maxAmount}
                        onChange={e => {
                            if (e.target.value !== '' && Number(e.target.value) > MAX_INSTANT_DISCOUNT_AMOUNT) return;
                            handleDiscountChange('maxAmount', e.target.value);
                        }}
                        inputProps={{ min: 1, max: MAX_INSTANT_DISCOUNT_AMOUNT }}
                        error={Boolean(maxAmountError)}
                        helperText={maxAmountError}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Applicable Days"
                        disabled={!allowEdit ? (DisableFlag) : false}
                        select
                        fullWidth
                        required
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => {
                                if (selected.includes('All') || selected.length === daysOfWeek.length) {
                                    return "Whole Week";
                                }
                                return selected.filter(val => val !== 'All').join(', ');
                            }
                        }}
                        InputLabelProps={{ shrink: true }}
                        value={
                            applicableDays.length === daysOfWeek.length &&
                            daysOfWeek.every(day => applicableDays.includes(day))
                                ? ['All', ...applicableDays]
                                : applicableDays
                        }
                        onChange={e => {
                            const value = e.target.value;
                            const currentDays = applicableDays;
                            const wasAllSelected = currentDays.length === daysOfWeek.length && daysOfWeek.every(day => currentDays.includes(day));

                            if (value.includes('All')) {
                                if (!wasAllSelected) {
                                    handleDiscountChange('applicableDays', daysOfWeek);
                                } else {
                                    const remainingDays = value.filter(day => day !== 'All');
                                    handleDiscountChange('applicableDays', remainingDays);
                                }
                            } else {
                                if (wasAllSelected) {
                                    handleDiscountChange('applicableDays', []);
                                } else {
                                    handleDiscountChange('applicableDays', value.filter(day => day !== 'All'));
                                }
                            }
                        }}
                    >
                        <MenuItem key="All" value="All">Whole Week</MenuItem>
                        {daysOfWeek.map(day => (
                            <MenuItem key={day} value={day}>{day}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                disabled={!allowEdit ? (DisableFlag) : false}
                                checked={isRuleHourEnabled}
                                onChange={e => handleDiscountChange('isRuleHourEnable', e.target.checked)}
                            />
                        }
                        label="Rule Hour Enabled"
                    />
                </Grid>
                {isRuleHourEnabled ? (
                    <Grid item xs={12} display={"flex"} gap={2}>
                        <Grid item xs={3}>
                            <TextField
                                label="Rule Start Time"
                                type="time"
                                disabled={!allowEdit ? (DisableFlag) : false}
                                fullWidth
                                required
                                InputLabelProps={{ shrink: true }}
                                value={ruleHour.startTime || ''}
                                onChange={e => handleDiscountChange('ruleHour.startTime', e.target.value)}

                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                label="Rule End Time"
                                type="time"
                                disabled={!allowEdit ? (DisableFlag) : false}
                                fullWidth
                                required
                                InputLabelProps={{ shrink: true }}
                                value={ruleHour.endTime || ''}
                                onChange={e => handleDiscountChange('ruleHour.endTime', e.target.value)}

                            />
                        </Grid>
                    </Grid>
                ) : null}
                <Grid item xs={6}>
                    <TextField
                        label="Discount Funding Type"
                        disabled={!allowEdit ? (DisableFlag) : false}
                        select
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        value={discountFundingType}
                        onChange={e => handleDiscountChange('discountFundingType', e.target.value)}
                    >
                        <MenuItem value="0">Fixed</MenuItem>
                        <MenuItem value="1">Percent</MenuItem>
                    </TextField>
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        label="Merchant Value"
                        type="number"
                        disabled={!allowEdit ? (DisableFlag) : false}
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        value={discountFunding.merchantValue}
                        onChange={e => handleDiscountChange('discountFunding.merchantValue', e.target.value)}
                        inputProps={{ min: 0 }}
                        error={!viewMode && (isAllFundingZero || Boolean(merchantFundingError))}
                        helperText={!viewMode && (merchantFundingError || (isAllFundingZero ? fundingErrorMsg : ''))}
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        label="Issuer Value"
                        type="number"
                        disabled={!allowEdit ? (DisableFlag) : false}
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        value={discountFunding.issuerValue}
                        onChange={e => handleDiscountChange('discountFunding.issuerValue', e.target.value)}
                        inputProps={{ min: 0 }}
                        error={!viewMode && (isAllFundingZero || Boolean(issuerFundingError))}
                        helperText={!viewMode && (issuerFundingError || (isAllFundingZero ? fundingErrorMsg : ''))}
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        label="Brand Value"
                        type="number"
                        disabled={!allowEdit ? (DisableFlag) : false}
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        value={discountFunding.brandValue}
                        onChange={e => handleDiscountChange('discountFunding.brandValue', e.target.value)}
                        inputProps={{ min: 0 }}
                        error={!viewMode && (isAllFundingZero || Boolean(brandFundingError))}
                        helperText={!viewMode && (brandFundingError || (isAllFundingZero ? fundingErrorMsg : ''))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                disabled={!allowEdit ? (DisableFlag) : false}
                                checked={isVelocityCheckEnabled}
                                onChange={e => handleDiscountChange('isVelocityCheckEnabled', e.target.checked)}
                            />
                        }
                        label="Velocity Check Enabled"
                    />
                </Grid>
                {isVelocityCheckEnabled ? (
                    <Grid item xs={12} display={"flex"} gap={2}>
                        <Grid item xs={3}>
                            <TextField
                                label="Max Transaction Count"
                                type="number"
                                disabled={!allowEdit ? (DisableFlag) : false}
                                fullWidth
                                required
                                InputLabelProps={{ shrink: true }}
                                value={hasValue(velocityCheck.maxTransactionCount) ? velocityCheck.maxTransactionCount : ''}
                                onChange={e => handleDiscountChange('velocityCheck.maxTransactionCount', e.target.value)}
                                inputProps={{ min: 1, max: MAX_VELOCITY_TRANSACTION_COUNT, step: 1 }}
                                error={Boolean(maxTransactionCountError)}
                                helperText={maxTransactionCountError}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                label="Validity Type"
                                disabled={!allowEdit ? (DisableFlag) : false}
                                select
                                fullWidth
                                required
                                InputLabelProps={{ shrink: true }}
                                value={velocityCheck.validityType}
                                onChange={e => handleDiscountChange('velocityCheck.validityType', e.target.value)}

                            >
                                <MenuItem value="0">Daily</MenuItem>
                                <MenuItem value="1">Weekly</MenuItem>
                                <MenuItem value="2">Monthly</MenuItem>
                                <MenuItem value="3">Complete</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                ) : null}
            </Grid>
              <Grid container className="step_action">
                <Grid item xs={8} justify="flex-start" className={classes.addRange}>
                </Grid>
                <Grid item xs={4} style={{ display: "flex", justifyContent: "space-evenly" }} justify="flex-end">
                    <Button 
                        onClick={() => setActiveStep(3)} 
                        disabled={createLoad}
                        className={`action_login_button`}
                    >
                        Back
                    </Button>
                    <Button 
                        onClick={() => !DisableFlag ? handleNext() : onChangeView("list")} 
                        disabled={!isViewMode && !instantDiscount.isNext}
                        className={`action_login_button ${(!isViewMode && !instantDiscount.isNext) ? "disabled_login_button" : ""}`}
                    >
                        {!DisableFlag ? (allowEdit ? "Modify" : "Create" ): "Close"}
                        {createLoad ? <CircularProgress color="secondary" style={{
                            "width": "25px", "height": "25px", "marginLeft": "6px", "color": "#fff"}}
                        /> : null}
                    </Button>
                </Grid>
            </Grid>
            <SnackbarToast
                openSnackbar={openSnackbar}
                handleSnackBarAction={handleSnackBarAction}
                snackbarMsg={snackbarMsg}
                type="error"
            />
        </React.Fragment>
    )
}

export default InstantDiscountStepper;
